/**
 * The tiny markdown subset the chat bubbles render.
 *
 * It lives in its own module so the escaping rules can be tested without a DOM:
 * this is the only place in the widget where text from the network reaches
 * innerHTML, so it is the one function that has to be right.
 */

/**
 * Schemes a markdown link is allowed to use.
 *
 * This is an allowlist, not a blocklist: anything that does not begin with one
 * of these is rendered as plain text. A blocklist would miss `JaVaScRiPt:`,
 * leading whitespace and entity-encoded variants.
 */
export function isSafeHref(href: string): boolean {
  return /^(https?:\/\/|mailto:|tel:|\/|#)/i.test(href.trim());
}

export function parseMarkdown(text: string): string {
  return text
    // Escape HTML. The quote characters matter as much as the angle brackets:
    // the link rule below puts the URL inside an href="..." attribute, and an
    // unescaped quote there closes the attribute and turns the rest of the URL
    // into an event handler. `[x](" onmouseover="alert(1))` produced a live
    // onmouseover handler before this was fixed.
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    // Code blocks
    .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Bold
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    // Links. An unsafe scheme keeps its label and loses the link entirely,
    // which is what a reader should see for `[click me](javascript:...)`.
    .replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      (_match: string, label: string, href: string) =>
        isSafeHref(href)
          ? `<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`
          : label
    )
    // Line breaks
    .replace(/\n/g, '<br>');
}
